import React from 'react';
import { Check, X } from 'phosphor-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CartItem } from '@/types/cart.types';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import './OrderSuccess.css';

interface OrderSuccessState {
  orderId: string;
  items: CartItem[];
  totalAmount: number;
}

const OrderSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const orderData = location.state as OrderSuccessState;

  if (!orderData) {
    return (
      <div className="os" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
          Loading order details…
        </span>
      </div>
    );
  }

  const totalItems = orderData.items.reduce((t, item) => t + item.quantity, 0);

  return (
    <div className="os">
      <div className="os__glow" />

      {/* Top bar: theme toggle + close */}
      <div className="os__top-bar">
        <ThemeToggle />
        <button className="os__close" onClick={() => navigate('/home')}>
          <X size={16} color="currentColor" weight="bold" />
        </button>
      </div>

      {/* Success icon */}
      <motion.div
        className="os__icon-area"
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.175, 0.885, 0.32, 1.275] }}
      >
        <div className="os__ring os__ring--5" />
        <div className="os__ring os__ring--4" />
        <div className="os__ring os__ring--3" />
        <div className="os__ring os__ring--2" />
        <div className="os__ring os__ring--1" />
        <div className="os__check">
          <Check size={30} weight="bold" color="#0b0b0a" />
        </div>
      </motion.div>

      {/* Heading */}
      <motion.h1
        className="os__heading"
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.35, delay: 0.2 }}
      >
        Payment Successful
      </motion.h1>

      <motion.p
        className="os__sub"
        initial={{ y: 8, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        Collect your items from the kiosk
      </motion.p>

      {/* Order card */}
      <motion.div
        className="os__card"
        initial={{ y: 16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.35, delay: 0.4 }}
      >
        <div className="os__card-header">
          <span className="os__card-items">{totalItems} {totalItems === 1 ? 'Item' : 'Items'}</span>
          <span className="os__card-id">#{orderData.orderId}</span>
        </div>

        {orderData.items.map((item, i) => (
          <div key={i} className="os__card-row">
            <span className="os__item-name">{item.name}</span>
            <span className="os__item-qty">×{item.quantity}</span>
          </div>
        ))}
      </motion.div>

      {/* Home button */}
      <motion.button
        className="os__home-btn"
        onClick={() => navigate('/home')}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.55 }}
      >
        Back to Home
      </motion.button>
    </div>
  );
};

export default OrderSuccess;
